import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { FaUser, FaLock, FaCamera, FaSignOutAlt, FaTimes } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import api from "../services/api";

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [isLogoutMode, setIsLogoutMode] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [odometerNumber, setOdometerNumber] = useState('');
  const [odometerError, setOdometerError] = useState('');
  const [errorPopup, setErrorPopup] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [previousLogoutReading, setPreviousLogoutReading] = useState(null); // ✅ NEW
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is logged in
  useEffect(() => {
    const user = localStorage.getItem('currentUser');
    if (user) {
      const parsedUser = JSON.parse(user);
      setCurrentUser(parsedUser);
      setIsLoggedIn(true);
    }
  }, []);

  // Prevent back button after login
  useEffect(() => {
    const handlePopState = () => {
      const token = localStorage.getItem('token');
      if (token) {
        window.history.pushState(null, '', window.location.href);
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.history.pushState(null, '', window.location.href);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Handle logout trigger from Layout
  useEffect(() => {
    if (location.state?.triggerLogoutCamera && currentUser?.role === 'installer') {
      setIsLogoutMode(true);
      setShowCamera(true);
      setOdometerNumber('');
      setOdometerError('');
      setErrorPopup(null);
      setCapturedImage(null);
      setIsProcessing(false);

      setTimeout(() => {
        startCamera();
      }, 100);
    }
  }, [location.state, currentUser]);

  // ✅ NEW: Fetch previous logout reading when camera opens for login
  useEffect(() => {
    if (showCamera && !isLogoutMode && currentUser?.id) {
      fetchPreviousLogoutReading();
    }
  }, [showCamera, isLogoutMode, currentUser]);

  // ✅ NEW: Function to fetch previous logout reading
  const fetchPreviousLogoutReading = async () => {
    if (!currentUser?.id) return;
    
    try {
      const response = await api.get(`/installer-session/last/${currentUser.id}`);
      if (response.data && response.data.logout_odometer_reading) {
        setPreviousLogoutReading(Number(response.data.logout_odometer_reading));
      } else {
        setPreviousLogoutReading(null);
      }
    } catch (error) {
      setPreviousLogoutReading(null);
    }
  };

  // Store login reading for later validation
  const storeLoginReading = (reading) => {
    sessionStorage.setItem('loginOdometerReading', reading);
  };

  const performLogout = () => {
    logout();
    
    sessionStorage.removeItem('odometerImage');
    sessionStorage.removeItem('odometerCaptureTime');
    sessionStorage.removeItem('logoutOdometerImage');
    sessionStorage.removeItem('logoutOdometerTime');
    sessionStorage.removeItem('loginOdometerReading');
    
    setIsLoggedIn(false);
    setCurrentUser(null);
    setUsername('');
    setPassword('');
    setError('');
    setShowCamera(false);
    setCapturedImage(null);
    setIsLogoutMode(false);
    setPendingNavigation(null);
    setOdometerNumber('');
    setOdometerError('');
    setErrorPopup(null);
    setIsProcessing(false);
    setPreviousLogoutReading(null); // ✅ Reset
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    navigate('/login', { replace: true });
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      console.error('❌ Error accessing camera:', err);
      setError('Unable to access camera. Please ensure camera permissions are granted.');
      setShowCamera(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const result = login(username, password);
    
    if (result.success) {
      const { user } = result;
      
      setCurrentUser(user);
      setIsLoggedIn(true);
    
      if (username === 'query' && password === 'query') {
        navigate('/log-page');
        return; 
      }
      
      if (user.role === 'installer') {
        setIsLogoutMode(false);
        setPendingNavigation('/vehicle-details');
        setShowCamera(true);
        setOdometerNumber('');
        setOdometerError('');
        setErrorPopup(null);
        setCapturedImage(null);
        setIsProcessing(false);
        setPreviousLogoutReading(null); // ✅ Reset, will be fetched in useEffect
        setTimeout(() => startCamera(), 100);
        return;
      }
      
      if (user.role === 'teamlead') {
        navigate('/installer-signup');
        return;
      }
      
      if (user.role === 'super_admin' || user.role === 'admin') {
        navigate('/dashboard');
      } else {
        navigate('/');
      }
    } else {
      setError(result.message);
    }
  };

  // ✅ MODIFIED: Validate odometer with previous logout reading
  const validateOdometer = () => {
    if (!odometerNumber.trim()) {
      setOdometerError('Odometer reading is required');
      return false;
    }
    if (isNaN(odometerNumber.trim())) {
      setOdometerError('Odometer reading must be a number');
      return false;
    }
    if (Number(odometerNumber.trim()) <= 0) {
      setOdometerError('Odometer reading must be a positive number');
      return false;
    }
    
    // ✅ NEW: Validate against previous logout reading
    if (!isLogoutMode && previousLogoutReading !== null) {
      const currentReading = Number(odometerNumber.trim());
      if (currentReading < previousLogoutReading) {
        setOdometerError(`Reading must be ≥ ${previousLogoutReading} (previous logout reading)`);
        return false;
      }
    }
    
    setOdometerError('');
    return true;
  };

  // Login: Capture login odometer image
  const captureLoginImage = () => {
    if (!validateOdometer()) {
      return;
    }

    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImage(imageData);
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      handleLoginSuccess(imageData);
    }
  };

  const handleLoginSuccess = async (imageData) => {
    setIsProcessing(true);
    const loginTime = new Date().toISOString();

    // Store login reading for logout validation
    storeLoginReading(odometerNumber);

    // Store in session storage for backup
    sessionStorage.setItem("odometerImage", imageData);
    sessionStorage.setItem("odometerCaptureTime", loginTime);
    sessionStorage.setItem("odometerNumber", odometerNumber);

    try {
      const response = await api.post("/installer-session/login", {
        installerId: currentUser.id,
        installerName: currentUser.username,
        loginOdometerImage: imageData,
        odometerNumber: odometerNumber || ''
      });

      if (response.data && response.data.id) {
        localStorage.setItem("installerSessionId", response.data.id);
      } else {
        console.error('❌ No session ID returned from backend');
      }

      setShowCamera(false);
      setCapturedImage(null);
      setOdometerNumber('');
      setOdometerError('');
      setIsProcessing(false);

      if (pendingNavigation) {
        navigate(pendingNavigation, { replace: true });
      }
    } catch (err) {
      console.error('❌ Error during login session creation:', err);
      console.error('❌ Error details:', err.response?.data || err.message);
      setErrorPopup({
        message: err.response?.data?.error || 'Failed to create login session. Please try again.',
        details: err.response?.data?.details || null
      });
      setIsProcessing(false);
      // ✅ Don't close camera so user can retry
    }
  };

  const handleLogoutClick = () => {
    if (currentUser?.role === 'admin' || currentUser?.role === 'super_admin' || currentUser?.role === 'teamlead') {
      console.log('Admin/TeamLead logout - direct logout');
      performLogout();
      return;
    }
    
    if (currentUser?.role === 'installer') {
      setIsLogoutMode(true);
      setShowCamera(true);
      setOdometerNumber('');
      setOdometerError('');
      setErrorPopup(null);
      setCapturedImage(null);
      setIsProcessing(false);
      setTimeout(() => startCamera(), 100);
      return;
    }
    
    performLogout();
  };

  // Logout: Capture logout odometer image (without vehicle mileage)
  const captureLogoutImage = async () => {
    // Validate only odometer reading
    if (!validateOdometer()) {
      return;
    }

    setIsProcessing(true);

    const loginReading = parseFloat(sessionStorage.getItem('loginOdometerReading') || '0');
    const logoutReading = parseFloat(odometerNumber);

    // CRITICAL VALIDATION: Logout reading MUST be greater than login reading
    if (logoutReading <= loginReading) {
      setErrorPopup({
        message: `Logout odometer reading (${logoutReading}) must be GREATER than login reading (${loginReading})`,
        details: {
          loginReading,
          logoutReading,
          difference: logoutReading - loginReading
        }
      });
      setIsProcessing(false);
      return;
    }

    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const context = canvas.getContext("2d");
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = canvas.toDataURL("image/jpeg", 0.8);

      const sessionId = localStorage.getItem("installerSessionId");
      
      if (!sessionId) {
        console.error('❌ No session ID found in localStorage');
        setErrorPopup({
          message: 'Session not found. Please login again.',
          details: null
        });
        setIsProcessing(false);
        return;
      }

      try {
        const response = await api.post("/installer-session/logout", {
          sessionId: sessionId,
          logoutOdometerImage: imageData,
          logoutOdometerNumber: odometerNumber || ''
        });

        if (response.data) {
          setIsProcessing(false);
          performLogout();
        }
      } catch (err) {
        console.error('❌ Error during logout:', err);
        console.error('❌ Error details:', err.response?.data || err.message);
        
        setErrorPopup({
          message: err.response?.data?.error || 'Failed to complete logout. Please try again.',
          details: err.response?.data?.details || null
        });
        setIsProcessing(false);
      }
    }
  };

  const closeCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
    setCapturedImage(null);
    setIsLogoutMode(false);
    setPendingNavigation(null);
    setOdometerNumber('');
    setOdometerError('');
    setErrorPopup(null);
    setIsProcessing(false);
    setPreviousLogoutReading(null); // ✅ Reset
  };

  // Prevent closing camera without completing login
  const handleCancel = () => {
    if (isLogoutMode) {
      // For logout, allow cancel
      closeCamera();
    } else {
      // For login, show error and don't close
      setErrorPopup({
        message: 'You must enter the odometer reading and capture the image to login',
        details: null
      });
    }
  };

  return (
    <>
      <div
        className="min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://plus.unsplash.com/premium_photo-1681433394443-f00c5d39087e?q=80&w=1954&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D')`,
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>

        {showCamera && currentUser?.role === 'installer' ? (
          <div className="relative z-20 bg-white rounded-3xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">
                {isLogoutMode ? '📸 Logout - Enter Odometer Reading' : '📸 Login - Enter Odometer Reading'}
              </h3>
              <button
                onClick={isLogoutMode ? closeCamera : handleCancel}
                className="text-gray-500 hover:text-gray-700 text-2xl"
                disabled={!isLogoutMode && isProcessing}
              >
                <FaTimes />
              </button>
            </div>
            
            {isLogoutMode && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Login Odometer Reading:</span>{' '}
                  <span className="text-blue-700 font-bold">
                    {sessionStorage.getItem('loginOdometerReading') || 'N/A'}
                  </span>
                </p>
                <p className="text-xs text-red-500 mt-1">
                  ⚠️ Logout odometer reading must be <span className="font-bold">GREATER</span> than login reading
                </p>
              </div>
            )}
            
            {!isLogoutMode && (
              <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Installer:</span>{' '}
                  <span className="text-green-700 font-bold">{currentUser?.username}</span>
                </p>
                {/* ✅ NEW: Show previous logout reading if available */}
                {previousLogoutReading !== null && (
                  <p className="text-sm text-gray-700 mt-1">
                    <span className="font-semibold">Previous Logout Reading:</span>{' '}
                    <span className="text-orange-600 font-bold">{previousLogoutReading}</span>
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  📸 Please enter the odometer reading and capture the image
                </p>
                {previousLogoutReading !== null ? (
                  <p className="text-xs text-amber-600 mt-1 font-semibold">
                    ⚠️ Login reading must be ≥ {previousLogoutReading} (last session's logout reading)
                  </p>
                ) : (
                  <p className="text-xs text-green-600 mt-1">
                    ✅ First login - any positive reading is accepted
                  </p>
                )}
              </div>
            )}
            
            {/* Odometer Reading Input Field */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Odometer Reading <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={odometerNumber}
                onChange={(e) => {
                  setOdometerNumber(e.target.value);
                  if (odometerError) setOdometerError('');
                  setErrorPopup(null);
                }}
                placeholder="Enter odometer reading..."
                className={`w-full border ${odometerError ? 'border-red-500' : 'border-gray-300'} rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition`}
                disabled={isProcessing}
              />
              {odometerError && (
                <p className="text-red-500 text-xs mt-1">{odometerError}</p>
              )}
              {/* ✅ NEW: Show requirement below input */}
              {!isLogoutMode && previousLogoutReading !== null && (
                <p className="text-xs text-gray-400 mt-1">
                  Must be ≥ <span className="font-bold text-orange-600">{previousLogoutReading}</span> (previous logout reading)
                </p>
              )}
              <p className="text-xs text-gray-400 mt-1">Mandatory field - Enter the current odometer reading</p>
            </div>
            
            <div className="relative bg-black rounded-xl overflow-hidden">
              <video
                ref={videoRef}
                className="w-full h-64 object-cover"
                autoPlay
                playsInline
              />
              <canvas ref={canvasRef} className="hidden" />
              
              {capturedImage && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                  <img 
                    src={capturedImage} 
                    alt="Captured Odometer" 
                    className="w-full h-64 object-contain"
                  />
                </div>
              )}
            </div>

            <div className="mt-4 flex gap-3">
              {!capturedImage ? (
                <>
                  <button
                    onClick={isLogoutMode ? captureLogoutImage : captureLoginImage}
                    disabled={isProcessing}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-xl shadow-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isProcessing ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        <FaCamera /> {isLogoutMode ? 'Capture & Logout' : 'Capture & Login'}
                      </>
                    )}
                  </button>
                  <button
                    onClick={isLogoutMode ? closeCamera : handleCancel}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-3 rounded-xl transition-all duration-200 disabled:opacity-50"
                    disabled={!isLogoutMode && isProcessing}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={closeCamera}
                  className="w-full bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-3 rounded-xl transition-all duration-200"
                >
                  Close
                </button>
              )}
            </div>

            {!capturedImage && (
              <p className="text-xs text-red-500 text-center mt-3">
                ⚠️ You must enter the odometer reading and capture the image
              </p>
            )}
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="relative glass-card p-10 rounded-3xl shadow-2xl w-full max-w-md mx-4 z-10"
          >
            <div className="flex justify-center mb-6">
              <img
                src="./Loginlogo.png"
                alt="Company Logo"
                className="h-20 w-auto object-contain drop-shadow-md Loginlogo"
              />
            </div>

            <h2 className="text-3xl font-bold text-white text-center mb-8 drop-shadow textcolor">
              GPS Inventory
            </h2>

            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            {!isLoggedIn ? (
              <>
                <div className="mb-5 relative">
                  <FaUser className="absolute left-3 top-3.5 text-gray-400" />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Username"
                    className="w-full pl-10 pr-4 py-3 bg-white/80 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition"
                    required
                  />
                </div>

                <div className="mb-8 relative">
                  <FaLock className="absolute left-3 top-3.5 text-gray-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    className="w-full pl-10 pr-4 py-3 bg-white/80 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                >
                  Log In
                </button>
              </>
            ) : (
              <div className="space-y-4">
                <div className="text-white text-center">
                  <p className="text-lg">Welcome, {currentUser?.username || 'User'}!</p>
                  <p className="text-sm opacity-75">Role: {currentUser?.role}</p>
                </div>
                
                <button
                  type="button"
                  onClick={handleLogoutClick}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <FaSignOutAlt /> 
                  {currentUser?.role === 'installer' ? 'Logout with Odometer' : 'Logout'}
                  {currentUser?.role === 'installer' && <FaCamera className="ml-1" />}
                </button>
              </div>
            )}
          </form>
        )}

        {/* Error Popup */}
        {errorPopup && (
          <div className="fixed top-4 right-4 z-50 max-w-md w-full animate-slide-in-right">
            <div className="bg-red-50 border-l-4 border-red-500 rounded-lg shadow-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-semibold text-red-800">
                    {isLogoutMode ? 'Invalid Odometer Reading' : 'Required Action'}
                  </h3>
                  <div className="mt-1 text-sm text-red-700">
                    <p>{errorPopup.message}</p>
                    {errorPopup.details && (
                      <div className="mt-2 p-2 bg-red-100 rounded-md text-xs">
                        {errorPopup.details.lastLogoutReading !== undefined && (
                          <p>Previous Logout Reading: <strong>{errorPopup.details.lastLogoutReading}</strong></p>
                        )}
                        {errorPopup.details.currentLoginReading !== undefined && (
                          <p>Current Login Reading: <strong>{errorPopup.details.currentLoginReading}</strong></p>
                        )}
                        {errorPopup.details.difference !== undefined && (
                          <p>Difference: <strong className="text-red-600">{errorPopup.details.difference}</strong></p>
                        )}
                        {errorPopup.details.loginReading !== undefined && (
                          <>
                            <p>Login Reading: <strong>{errorPopup.details.loginReading}</strong></p>
                            <p>Logout Reading: <strong>{errorPopup.details.logoutReading}</strong></p>
                            <p>Difference: <strong className="text-red-600">{errorPopup.details.difference}</strong></p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setErrorPopup(null)}
                  className="ml-4 flex-shrink-0 text-red-400 hover:text-red-600 transition"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default Login;